import type { ReactNode } from 'react';
import { Download, FileText, Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DataLoader } from '@/components/ui/DataLoader';
import { useDownloadDocument } from '@/hooks/react-query/useDocuments';
import {
  useFundTransactionDocuments,
  useUnlinkFundTransactionDocument,
} from '@/hooks/react-query/useFundTransactions';
import { DOCUMENT_TYPE_LABELS } from '@/types/document';
import type { FundTransactionDocument } from '@/types/fund-transactions';

interface FundTransactionDocumentListProps {
  fundId: string;
  transactionId: string;
  emptyState: ReactNode;
  renderRowActions: (document: FundTransactionDocument) => ReactNode;
}

export function FundTransactionDocumentList({
  fundId,
  transactionId,
  emptyState,
  renderRowActions,
}: FundTransactionDocumentListProps) {
  const downloadDocument = useDownloadDocument();
  const documentsQuery = useFundTransactionDocuments(fundId, transactionId);

  return (
    <DataLoader<FundTransactionDocument[]>
      query={documentsQuery}
      showEmptyState
      emptyComponent={emptyState}
      loadingClassName="p-3"
    >
      {(documents) => (
        <ul className="mt-3 space-y-2">
          {documents.map((doc) => (
            <li key={doc.id} className="flex items-center gap-2">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium truncate">
                {doc.document_name}
              </span>
              <span className="text-xs text-muted-foreground">
                {doc.document_types
                  .map((type) => DOCUMENT_TYPE_LABELS[type])
                  .join(', ')}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 ml-auto shrink-0"
                onClick={() =>
                  downloadDocument.mutate({
                    id: doc.document_id,
                    documentName: doc.document_original_filename,
                  })
                }
                disabled={downloadDocument.isPending}
                aria-label="Download document"
              >
                {downloadDocument.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
              {renderRowActions(doc)}
            </li>
          ))}
        </ul>
      )}
    </DataLoader>
  );
}

export function UnlinkFundTransactionDocumentButton({
  fundId,
  transactionId,
  linkId,
}: {
  fundId: string;
  transactionId: string;
  linkId: string;
}) {
  const unlink = useUnlinkFundTransactionDocument(fundId, transactionId);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0 text-destructive"
      onClick={() => unlink.mutate(linkId)}
      disabled={unlink.isPending}
      aria-label="Remove document"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  );
}
